/**
 * Site indents raised by the Resident Construction Manager.
 *
 * The BRD is explicit that a material requirement must reach the store-man
 * first — he checks his own store and only raises a purchase requisition for
 * what he genuinely cannot fulfil. This screen digitises that first hop.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft, ClipboardList, Eye, HardHat, PackageMinus, Pencil, Plus, Send, Trash2,
} from 'lucide-react'
import {
  Alert, Card, EmptyState, Field, Input, Loading, Select, StatusPill, Textarea, Th, sortRows, useSort, useToast,
} from '@/components/ui'
import { MaterialPicker, NotPermitted } from '@/components/docs/shared'
import { useAuth } from '@/lib/auth'
import {
  fetchLines, fetchOne, issuedAgainstIndent, useMaterials, useQuery, useSiteNames, useSites, useStock,
} from '@/lib/data'
import { supabase } from '@/lib/supabase'
import { DocumentDate, todayIso } from '@/components/docs/DocumentDate'
import { DeleteDocument } from '@/components/docs/DeleteDocument'
import { can, isHoRole, iso, seesManySites, visibleSites } from '@/lib/domain'
import { ddmmyyyy, qty } from '@/lib/format'
import { enqueue } from '@/lib/sync'
import type { Material } from '@/lib/types'

export function IndentList() {
  const { profile } = useAuth()
  const role = profile!.role
  const ho = seesManySites(role)
  // A Project Manager picking a colleague's site would see nothing, which
  // looks like a fault rather than a boundary. Offer him only his own.
  const [site, setSite] = useState(ho ? '' : (profile!.site_id ?? ''))
  const { data: sites } = useSites()
  const { siteName } = useSiteNames()
  const mySites = useMemo(() => visibleSites(sites, role, profile!.id), [sites, role, profile])

  const { data, loading, error } = useQuery<any>('indents', () => {
    let q = supabase.from('indents').select('*')
    if (site) q = q.eq('site_id', site)
    return q.order('indent_date', { ascending: false }).limit(300)
  }, [site])

  const canConvert = can(role, 'createPr')

  const { sort, toggle } = useSort('indent_date')
  const sorted = useMemo(() => sortRows(data, sort, {
    indent_date: 'date', required_by: 'date',
  }), [data, sort])

  return (
    <div className="space-y-4">
      <Card
        title="Site Indents"
        subtitle="What the RCM has asked the store-man for"
        actions={can(role, 'raiseIndent') && (
          <Link to="/indents/new" className="btn-primary btn-sm"><Plus className="h-4 w-4" /> Raise indent</Link>
        )}
      >
        <Alert tone="info">
          An indent is the RCM telling the store-man what the work needs. The store-man issues it from his own
          store where he can, and rolls the rest into a purchase requisition.
        </Alert>
        {ho && (
          <div className="mt-3 max-w-xs">
            <Select value={site} onChange={(e) => setSite(e.target.value)} placeholder="All sites"
                    options={mySites.map((s) => ({ value: s.id, label: s.site_name }))} />
          </div>
        )}
      </Card>

      <Card bodyClass="p-0 sm:p-0">
        {loading && <Loading />}
        {error && <div className="p-4"><EmptyState title="Could not load indents" message={error} /></div>}
        {!loading && !error && data.length === 0 && (
          <EmptyState icon={<HardHat className="h-9 w-9" />} title="No indents raised yet" />
        )}
        {data.length > 0 && (
          <div className="table-wrap mx-0">
            <table className="tbl">
              <thead>
                <tr><Th id="id" sort={sort} onSort={toggle}>Indent</Th><Th id="site_id" sort={sort} onSort={toggle}>Site</Th><Th id="raised_by_name" sort={sort} onSort={toggle}>Raised by</Th><Th id="indent_date" sort={sort} onSort={toggle}>Date</Th><Th id="required_by" sort={sort} onSort={toggle}>Needed by</Th>
                    <Th id="urgency" sort={sort} onSort={toggle}>Urgency</Th><Th id="status" sort={sort} onSort={toggle}>Status</Th><Th id="work_description" sort={sort} onSort={toggle}>Work</Th><th /></tr>
              </thead>
              <tbody>
                {sorted.map((d: any) => (
                  <tr key={d.id} className="cursor-pointer hover:bg-slate-50">
                    <td className="font-mono text-xs font-semibold">
                      <Link to={`/indents/${d.id}`} className="text-oval-700 hover:underline">{d.id}</Link>
                    </td>
                    <td className="text-xs" title={d.site_id}>{siteName(d.site_id)}</td>
                    <td className="text-xs">{d.raised_by_name ?? '—'}</td>
                    <td className="text-xs">{ddmmyyyy(d.indent_date)}</td>
                    <td className="text-xs">{ddmmyyyy(d.required_by)}</td>
                    <td className="text-xs">{d.urgency}</td>
                    <td><StatusPill status={d.status} /></td>
                    <td className="max-w-[220px] truncate text-xs text-slate-500">{d.work_description ?? '—'}</td>
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link to={`/indents/${d.id}`} className="btn-ghost btn-sm">
                          <Eye className="h-3 w-3" /> Open
                        </Link>
                        {canConvert && d.status === 'SUBMITTED' && (
                          <Link to={`/prs/new?from_indent=${d.id}`} className="btn-secondary btn-sm">
                            <ClipboardList className="h-3 w-3" /> Raise PR
                          </Link>
                        )}
                        {d.converted_pr_id && (
                          <Link to={`/prs/${d.converted_pr_id}`} className="font-mono text-xs text-oval-700 hover:underline">
                            {d.converted_pr_id}
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}

// ---------------------------------------------------------------------------
interface Line { key: string; material_id: string | null; quantity: string; remarks: string }
const blank = (): Line => ({ key: crypto.randomUUID(), material_id: null, quantity: '', remarks: '' })

export function IndentNew() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()
  // Reused as the edit screen for a draft: /indents/:id/edit
  const { id: editId } = useParams()
  const editing = !!editId

  const role = profile!.role
  const ho = isHoRole(role)
  const { data: sites } = useSites()
  const { data: materials } = useMaterials()

  const [siteId, setSiteId] = useState(profile!.site_id ?? 'HO')
  const [indentDate, setIndentDate] = useState(todayIso)
  const [requiredBy, setRequiredBy] = useState(() => { const d = new Date(); d.setDate(d.getDate() + 3); return iso(d) })
  const [urgency, setUrgency] = useState('Normal')
  const [work, setWork] = useState('')
  const [remarks, setRemarks] = useState('')
  const [lines, setLines] = useState<Line[]>([blank()])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { data: stock } = useStock(siteId)
  const byId = useMemo(
    () => Object.fromEntries(materials.map((m) => [m.id, m])) as Record<string, Material>, [materials])

  // Pull the draft back onto the form.
  useEffect(() => {
    if (!editId) return
    let live = true
    void (async () => {
      const { data: h } = await supabase.from('indents').select('*').eq('id', editId).single()
      const { data: ls } = await supabase.from('indent_line_items')
        .select('*').eq('indent_id', editId).order('sl_no')
      if (!live || !h) return
      setSiteId(h.site_id); setRequiredBy(h.required_by ?? requiredBy)
      setUrgency(h.urgency ?? 'Normal'); setWork(h.work_description ?? '')
      setRemarks(h.remarks ?? '')
      setLines((ls ?? []).length
        ? (ls ?? []).map((l: any) => ({
            key: crypto.randomUUID(), material_id: l.material_id,
            quantity: String(l.quantity ?? ''), remarks: l.remarks ?? '',
          }))
        : [blank()])
    })()
    return () => { live = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId])

  if (!can(role, 'raiseIndent')) {
    return <NotPermitted what="raise a site indent" who="Resident Construction Manager" />
  }

  async function submit(send = true) {
    const filled = lines.filter((l) => l.material_id && Number(l.quantity) > 0)
    if (filled.length === 0) { setError('Add at least one material with a quantity.'); return }
    // A draft is allowed to be incomplete — that is the point of one. Only a
    // document actually going to the store-man has to be whole.
    if (send && !work.trim()) {
      setError('Describe the work this material is for — the store-man needs it to prioritise.'); return
    }
    setError(null); setBusy(true)
    try {
      if (editing) {
        await enqueue({
          kind: 'indent.update', docType: 'INDENT', localDocId: editId!,
          label: `${send ? 'Send' : 'Save'} draft indent ${editId}`,
          payload: {
            indent_id: editId,
            header: {
              site_id: siteId, required_by: requiredBy, urgency,
              work_description: work, remarks: remarks || null,
            },
            lines: filled.map((l) => ({
              material_id: l.material_id,
              quantity: Number(l.quantity),
              uom: byId[l.material_id!]?.unit_of_measure ?? null,
              required_by: requiredBy,
              remarks: l.remarks || null,
            })),
            submit: send,
          },
        })
        toast.success(send ? 'Indent sent to the store-man.' : 'Draft updated.')
        navigate(send ? '/indents' : `/indents/${editId}`)
        return
      }

      await enqueue({
        kind: 'indent.create', docType: 'INDENT',
        label: `${send ? 'Site indent' : 'Draft indent'} at ${siteId} — ${filled.length} material(s)`,
        payload: {
          header: {
            site_id: siteId,
            raised_by: profile!.id,
            raised_by_name: profile!.full_name,
            indent_date: indentDate,
            required_by: requiredBy,
            urgency,
            work_description: work,
            status: send ? 'SUBMITTED' : 'DRAFT',
            remarks: remarks || null,
          },
          lines: filled.map((l, i) => ({
            sl_no: i + 1,
            material_id: l.material_id,
            quantity: Number(l.quantity),
            uom: byId[l.material_id!]?.unit_of_measure ?? null,
            required_by: requiredBy,
            remarks: l.remarks || null,
          })),
        },
      })
      toast.success(send
        ? 'Indent sent to the store-man.'
        : 'Saved as a draft. It stays in your list until you send it.')
      navigate('/indents')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not raise the indent')
    } finally { setBusy(false) }
  }

  return (
    <div className="space-y-4">
      <Card title={editing ? 'Edit your draft indent' : 'Raise a site indent'}
            subtitle={editing
              ? 'Nothing has gone to the store-man yet — change whatever you like.'
              : 'Tell your store-man what the work needs and by when'}>
        {error && <div className="mb-4"><Alert tone="error">{error}</Alert></div>}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Site" required>
            <Select value={siteId} onChange={(e) => setSiteId(e.target.value)} disabled={!ho}
                    options={sites.map((s) => ({ value: s.id, label: `${s.site_name} (${s.site_code})` }))} />
          </Field>
          <DocumentDate label="Indent date" value={indentDate} onChange={setIndentDate} />
          <Field label="Needed by" required>
            <Input type="date" value={requiredBy} onChange={(e) => setRequiredBy(e.target.value)} />
          </Field>
          <Field label="Urgency" required>
            <Select value={urgency} onChange={(e) => setUrgency(e.target.value)}
                    options={['Low', 'Normal', 'High', 'Critical'].map((u) => ({ value: u, label: u }))} />
          </Field>
          <Field label="Work description" required className="sm:col-span-2 lg:col-span-4">
            <Input value={work} onChange={(e) => setWork(e.target.value)}
                   placeholder="e.g. Raft foundation reinforcement, block A, starting Monday" />
          </Field>
        </div>
      </Card>

      <Card title="Materials needed"
            actions={<button className="btn-secondary btn-sm" onClick={() => /* prepend: the blank row appears at the top so nobody scrolls to the bottom of a long document to type the next line. Agreed 09-09-2026. */ setLines((l) => [blank(), ...l])}>
              <Plus className="h-4 w-4" /> Add line</button>}>
        <div className="space-y-3">
          {lines.map((l, i) => {
            const m = l.material_id ? byId[l.material_id] : null
            const onHand = l.material_id ? (stock.find((s) => s.material_id === l.material_id)?.quantity_on_hand ?? 0) : null
            return (
              <div key={l.key} className="rounded-lg border border-slate-200 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-400">LINE {i + 1}</span>
                  {lines.length > 1 && (
                    <button className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                            onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <div className="grid gap-3 lg:grid-cols-12">
                  <Field label="Material" required className="lg:col-span-5">
                    <MaterialPicker value={l.material_id} autoFocus={false}
                                    onChange={(m) => setLines((ls) => ls.map((x) => x.key === l.key ? { ...x, material_id: m?.id ?? null } : x))}
                                    exclude={lines.filter((x) => x.key !== l.key).map((x) => x.material_id!).filter(Boolean)} />
                    {onHand !== null && m && (
                      <p className="hint">Site store currently holds {qty(onHand)} {m.unit_of_measure}</p>
                    )}
                  </Field>
                  <Field label={`Quantity${m ? ` (${m.unit_of_measure})` : ''}`} required className="lg:col-span-3">
                    <Input type="number" inputMode="decimal" min="0" step="any" value={l.quantity}
                           onChange={(e) => setLines((ls) => ls.map((x) => x.key === l.key ? { ...x, quantity: e.target.value } : x))} />
                  </Field>
                  <Field label="Remarks" className="lg:col-span-4">
                    <Input value={l.remarks}
                           onChange={(e) => setLines((ls) => ls.map((x) => x.key === l.key ? { ...x, remarks: e.target.value } : x))} />
                  </Field>
                </div>
              </div>
            )
          })}
        </div>
        <div className="mt-4">
          <Field label="Remarks for the store-man">
            <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
          </Field>
        </div>
      </Card>

      <div className="sticky bottom-20 z-10 flex justify-end gap-2 lg:bottom-4">
        <button className="btn-ghost" onClick={() => navigate('/indents')} disabled={busy}>Cancel</button>
        <button className="btn-secondary" onClick={() => submit(false)} disabled={busy}>
          Save as draft
        </button>
        <button className="btn-primary" onClick={() => submit(true)} disabled={busy}>
          <Send className="h-4 w-4" /> Send to store-man
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// One indent, in full.
//
// The RCM needs to see what he asked for and what happened to it. The
// store-man needs the same view plus the two things he can do about it:
// issue it out of his own store, or roll it into a purchase requisition.
// ---------------------------------------------------------------------------
export function IndentDetail() {
  const { id = '' } = useParams()
  const { profile } = useAuth()
  const role = profile!.role

  const [indent, setIndent] = useState<any>(null)
  const [lines, setLines] = useState<any[]>([])
  const [issues, setIssues] = useState<any[]>([])
  const [issued, setIssued] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const { data: materials } = useMaterials()
  const { data: sites } = useSites()
  const byId = useMemo(
    () => Object.fromEntries(materials.map((m) => [m.id, m])) as Record<string, Material>, [materials])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const header = await fetchOne<any>('indents', id)
      if (!header) throw new Error(`Indent ${id} was not found, or you do not have access to it.`)
      setIndent(header)
      setLines(await fetchLines<any>('indent_line_items', 'indent_id', id))
      // Any issue notes raised against this indent — the "fulfilled locally" path.
      const { data: mins } = await supabase.from('material_issue_notes')
        .select('id, status, issue_date, issued_to_name').eq('source_indent_id', id)
        .order('issue_date', { ascending: false })
      setIssues(mins ?? [])
      setIssued(await issuedAgainstIndent(id))
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the indent')
    } finally { setLoading(false) }
  }, [id])

  useEffect(() => { void load() }, [load])

  const { data: stock } = useStock(indent?.site_id ?? null)

  if (loading) return <Loading label="Loading indent…" />
  if (error || !indent) return <Alert tone="error">{error}</Alert>

  const site = sites.find((s) => s.id === indent.site_id)
  const open = indent.status === 'SUBMITTED' || indent.status === 'ACCEPTED'

  // Has any material already gone out of the store against this indent?
  const anyIssued = issues.some((m) => m.status === 'APPROVED')
  const balance = lines.reduce(
    (a, l) => a + Math.max(0, Number(l.quantity) - (issued[l.material_id] ?? 0)), 0)

  /**
   * The store-man gets one bite at the indent from his own shelves. Once an
   * issue note against it has been approved, whatever is left is procurement's
   * problem — otherwise he can keep issuing in dribs and drabs and nobody ever
   * raises the requisition for the balance.
   */
  const mine = indent.raised_by === profile!.id || role === 'admin'
  const canIssue = can(role, 'createMin') && open && !anyIssued
  const canConvert = can(role, 'createPr') && open && balance > 0.001

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <Link to="/indents" className="btn-ghost btn-sm"><ArrowLeft className="h-4 w-4" /> All indents</Link>
          <h1 className="font-mono text-base font-bold text-slate-800">{indent.id}</h1>
          <StatusPill status={indent.status} />
          {indent.urgency === 'High' && <span className="pill-red">High urgency</span>}
        </div>
        <div className="flex flex-wrap gap-2">
          {indent.status === 'DRAFT' && mine && (
            <Link to={`/indents/${indent.id}/edit`} className="btn-primary btn-sm">
              <Pencil className="h-4 w-4" /> Edit this draft
            </Link>
          )}
          {canIssue && (
            <Link to={`/mins/new?from_indent=${indent.id}`} className="btn-secondary btn-sm">
              <PackageMinus className="h-4 w-4" /> Issue from my store
            </Link>
          )}
          {canConvert && (
            <Link to={`/prs/new?from_indent=${indent.id}`} className="btn-primary btn-sm">
              <ClipboardList className="h-4 w-4" /> Raise purchase requisition
            </Link>
          )}
          <DeleteDocument docType="INDENT" docId={indent.id} status={indent.status}
                          creatorId={indent.raised_by} backTo="/indents" />
        </div>
      </div>

      {open && !anyIssued && (can(role, 'createPr') || can(role, 'createMin')) && (
        <Alert tone="info" title="Two ways to close this indent">
          Whatever you can supply from your own store, issue with a <strong>Material Issue Note</strong> —
          it never needs to go near procurement. Raise a <strong>purchase requisition</strong> only for the
          shortfall. If the whole indent is met from stock, the issue note closes it outright.
        </Alert>
      )}
      {open && anyIssued && (
        <Alert tone="info" title="Part of this indent has already been issued from the store">
          What you could supply has gone out on the issue note below. The store cannot be drawn on again
          for this indent — raise a <strong>purchase requisition</strong> for the balance, which is the
          <strong> Still to source</strong> column. The requisition will carry only that balance.
        </Alert>
      )}
      {indent.status === 'CONVERTED_TO_PR' && indent.converted_pr_id && (
        <Alert tone="success" title="Converted to a purchase requisition">
          This indent became{' '}
          <Link className="underline" to={`/prs/${indent.converted_pr_id}`}>{indent.converted_pr_id}</Link>.
        </Alert>
      )}
      {indent.status === 'FULFILLED_FROM_STOCK' && (
        <Alert tone="success" title="Met from site stock">
          The store-man issued this material out of the {site?.site_name ?? indent.site_id} store. It never
          became a requisition and nothing was purchased against it.
        </Alert>
      )}

      <Card title="Indent details">
        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm sm:grid-cols-4">
          <D label="Site" value={site?.site_name ?? indent.site_id} />
          <D label="Raised by" value={indent.raised_by_name ?? '—'} />
          <D label="Raised on" value={ddmmyyyy(indent.indent_date)} />
          <D label="Needed by" value={ddmmyyyy(indent.required_by)} />
          <D label="Urgency" value={indent.urgency ?? 'Normal'} />
          <D label="Handled on" value={indent.handled_at ? ddmmyyyy(indent.handled_at) : '—'} />
        </dl>
        {indent.work_description && (
          <div className="mt-4 rounded-lg bg-slate-50 px-3 py-2 text-sm">
            <p className="text-xs font-semibold text-slate-500">Work this material is for</p>
            <p className="text-slate-700">{indent.work_description}</p>
          </div>
        )}
        {indent.remarks && (
          <div className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-sm">
            <p className="text-xs font-semibold text-slate-500">Remarks</p>
            <p className="text-slate-700">{indent.remarks}</p>
          </div>
        )}
      </Card>

      <Card title="Materials asked for"
            subtitle="What has already gone out of the store, and what is still to be sourced">
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr><th>#</th><th>Material</th><th>Code</th><th className="num">Asked for</th>
                  <th className="num">Issued from store</th><th className="num">Still to source</th>
                  <th className="num">In store now</th>
                  <th>Needed by</th><th>Remarks</th></tr>
            </thead>
            <tbody>
              {lines.map((l) => {
                const m = byId[l.material_id]
                const onHand = stock.find((s) => s.material_id === l.material_id)?.quantity_on_hand ?? 0
                const gone = issued[l.material_id] ?? 0
                const left = Math.max(0, Number(l.quantity) - gone)
                return (
                  <tr key={l.id}>
                    <td className="text-xs text-slate-400">{l.sl_no}</td>
                    <td className="text-sm font-medium">{m?.item_name ?? l.material_id}</td>
                    <td className="font-mono text-xs text-slate-500">{m?.item_code ?? '—'}</td>
                    <td className="num">{qty(l.quantity)} {l.uom ?? m?.unit_of_measure}</td>
                    <td className={`num text-xs ${gone > 0 ? 'font-semibold text-emerald-700' : 'text-slate-400'}`}>
                      {gone > 0 ? qty(gone) : '—'}
                    </td>
                    <td className={`num text-xs font-semibold ${left > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                      {left > 0 ? qty(left) : 'met'}
                    </td>
                    <td className={`num text-xs ${onHand > 0 ? 'text-slate-600' : 'text-slate-400'}`}>
                      {qty(onHand)}
                    </td>
                    <td className="text-xs">{l.required_by ? ddmmyyyy(l.required_by) : '—'}</td>
                    <td className="max-w-[200px] truncate text-xs text-slate-500">{l.remarks ?? '—'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {issues.length > 0 && (
        <Card title="Issued from site stock against this indent">
          <ul className="space-y-1">
            {issues.map((m) => (
              <li key={m.id} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2 text-sm">
                <Link to={`/mins/${m.id}`} className="font-mono text-xs text-oval-700 hover:underline">{m.id}</Link>
                <StatusPill status={m.status} />
                <span className="text-xs text-slate-500">{ddmmyyyy(m.issue_date)}</span>
                <span className="min-w-0 flex-1 truncate text-xs text-slate-500">to {m.issued_to_name ?? '—'}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

    </div>
  )
}

const D = ({ label, value }: { label: string; value: string }) => (
  <div>
    <dt className="text-xs font-semibold text-slate-500">{label}</dt>
    <dd className="mt-0.5 break-words text-slate-800">{value}</dd>
  </div>
)
